/**
 * Single chokepoint for display date/time formatting.
 *
 * All screens format dates through the four exported helpers so the shape is
 * consistent, and the active locale follows a user-selectable preset
 * (persisted per-device in localStorage). React-free on purpose so services
 * and tests can import it; the live-update hook lives in ./useDatePreset.
 */
export type DatePresetKey = 'system' | 'dmy' | 'mdy' | 'iso';

export interface DatePreset {
  key: DatePresetKey;
  labelKey: string; // i18n key for the option label
  locale: string | undefined; // undefined => machine locale
}

export const DATE_PRESETS: DatePreset[] = [
  { key: 'system', labelKey: 'dateFormat.system', locale: undefined },
  { key: 'dmy', labelKey: 'dateFormat.dmy', locale: 'en-GB' },
  { key: 'mdy', labelKey: 'dateFormat.mdy', locale: 'en-US' },
  { key: 'iso', labelKey: 'dateFormat.iso', locale: 'en-CA' },
];

const STORAGE_KEY = 'citrus.dateFormat';
const VALID_KEYS = new Set<DatePresetKey>(['system', 'dmy', 'mdy', 'iso']);

function readStoredPreset(): DatePresetKey {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (raw && VALID_KEYS.has(raw as DatePresetKey)) return raw as DatePresetKey;
  } catch {
    /* localStorage unavailable (SSR / restricted context) */
  }
  return 'system';
}

let activePreset: DatePresetKey = readStoredPreset();

function localeFor(key: DatePresetKey): string | undefined {
  return DATE_PRESETS.find(p => p.key === key)?.locale;
}

// --- listener store (consumed by ./useDatePreset for live re-render) ---
const listeners = new Set<() => void>();

export function subscribeDatePreset(cb: () => void): () => void {
  listeners.add(cb);
  return () => {
    listeners.delete(cb);
  };
}

export function getDatePreset(): DatePresetKey {
  return activePreset;
}

export function setDatePreset(key: DatePresetKey): void {
  if (!VALID_KEYS.has(key)) return;
  activePreset = key;
  try {
    localStorage.setItem(STORAGE_KEY, key);
  } catch {
    /* ignore persistence failure */
  }
  listeners.forEach(l => l());
}

// --- formatters ---
export interface DateFormatOptions {
  timeZone?: string;
}

function toDate(value: string | Date): Date | null {
  if (value === null || value === undefined) return null;
  const d = typeof value === 'string' ? new Date(value) : value;
  return isNaN(d.getTime()) ? null : d;
}

function fmt(
  value: string | Date,
  base: Intl.DateTimeFormatOptions,
  opts?: DateFormatOptions,
): string {
  const d = toDate(value);
  if (!d) return 'N/A';
  try {
    return new Intl.DateTimeFormat(localeFor(activePreset), {
      ...base,
      timeZone: opts?.timeZone || undefined,
    }).format(d);
  } catch (err) {
    console.error('Error formatting date:', err);
    return 'N/A';
  }
}

const DATE_OPTS: Intl.DateTimeFormatOptions = { day: '2-digit', month: '2-digit', year: 'numeric' };
const TIME_OPTS: Intl.DateTimeFormatOptions = { hour: '2-digit', minute: '2-digit', hour12: false };
const SHORT_OPTS: Intl.DateTimeFormatOptions = { weekday: 'short', day: 'numeric', month: 'short' };

export function formatDate(value: string | Date, opts?: DateFormatOptions): string {
  return fmt(value, DATE_OPTS, opts);
}

export function formatTime(value: string | Date, opts?: DateFormatOptions): string {
  return fmt(value, TIME_OPTS, opts);
}

export function formatDateShort(value: string | Date, opts?: DateFormatOptions): string {
  return fmt(value, SHORT_OPTS, opts);
}

export function formatDateTime(value: string | Date, opts?: DateFormatOptions): string {
  const out = fmt(value, { ...DATE_OPTS, ...TIME_OPTS }, opts);
  if (out === 'N/A') return out;
  // Locales insert their own date/time separator (e.g. "26/04/2026, 16:30"
  // or "... at ..."). Normalise to one space so the label reads as a unit.
  return out.replace(/\s+at\s+/, ' ').replace(', ', ' ').replace(',', '');
}

/**
 * A round's month arrives as an integer 1-12, not a date, so it cannot go
 * through the date formatters above. Any value outside that range renders as
 * the dash this app uses for "not known".
 *
 * `locale` lets a caller resolve the name from the UI language (i18next)
 * rather than the date-format preset above. That preset governs date
 * ORDERING (day-first vs month-first, separators) — it carries no language
 * choice, so it cannot supply a non-English month name. A caller that wants
 * the month spelled in the reader's language must pass its locale
 * explicitly. Omitting it falls back to this file's usual preset-based
 * resolution, matching every other formatter here.
 */
export function formatMonthName(month: number | null | undefined, locale?: string): string {
  if (typeof month !== 'number' || !Number.isInteger(month) || month < 1 || month > 12) return '—';
  // Day 1 of an arbitrary non-leap year: only the month part is read.
  const d = new Date(Date.UTC(2021, month - 1, 1));
  try {
    return new Intl.DateTimeFormat(locale ?? localeFor(activePreset), {
      month: 'long',
      timeZone: 'UTC',
    }).format(d);
  } catch (err) {
    console.error('Error formatting month:', err);
    return '—';
  }
}
